import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import DSDialog from '../../../../design-system/DSDialog';
import DSDialogTitle from '../../../../design-system/DSDialogTitle';
import DSDialogContent from '../../../../design-system/DSDialogContent';
import DSDialogActions from '../../../../design-system/DSDialogActions';
import DSBox from '../../../../design-system/DSBox';
import DSSlider from '../../../../design-system/DSSlider';
import DSPrimaryButton from '../../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../../design-system/DSSecondaryButton';
import DSHeaderTypography from '../../../../design-system/Typography/DSHeaderTypography';
import DSValueTypography from '../../../../design-system/Typography/DSValueTypography';
import {
  PENDULUM_BOB_DIAMETER_PX,
  PENDULUM_LOGO_SIZE_MIN,
  PENDULUM_LOGO_SIZE_MAX,
} from '../../../../consts/pendulumLogo';

// Scales the device's real 410px bob down to a size that fits comfortably in
// a modal. Purely a display zoom - the logo/bob ratio stays exact, so the
// preview is proportionally identical to the physical clock.
const PREVIEW_SCALE = 0.75;

// The bob's own color depends on whichever klydo is currently playing on the
// device, not on the collection - this is a neutral stand-in for preview only.
const PREVIEW_BOB_COLOR = '#2b2b2b';

export type PendulumLogoSizeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  logoUrl: string;
  // null/undefined both mean "no size chosen" - the slider still needs a
  // concrete starting position, so it defaults to PENDULUM_LOGO_SIZE_MIN here
  // (200px): the fixed size every clock drew before this feature existed, so
  // opening the modal and saving without changing anything is a true no-op.
  initialSize: number | null | undefined;
  onConfirm: (size: number) => void;
  onReset: () => void;
};

const PendulumLogoSizeModal = ({
  isOpen,
  onClose,
  logoUrl,
  initialSize,
  onConfirm,
  onReset,
}: PendulumLogoSizeModalProps) => {
  const { spacing, palette } = useTheme();
  const [tempSize, setTempSize] = useState(
    initialSize ?? PENDULUM_LOGO_SIZE_MIN,
  );
  // The uploaded logo's real pixel size, measured client-side from the same
  // preview <img>. `pad` upscales as well as down, so a chosen size larger
  // than the real image blurs it - warn instead of doing that silently.
  // Meaningful again as of the collection_logo_original upload preset
  // (TK-621) - uploads keep their real resolution instead of always being
  // capped at 200x200.
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTempSize(initialSize ?? PENDULUM_LOGO_SIZE_MIN);
    }
  }, [isOpen, initialSize]);

  const handleCancel = () => {
    setTempSize(initialSize ?? PENDULUM_LOGO_SIZE_MIN);
    onClose();
  };

  const handleSave = () => {
    onConfirm(tempSize);
    onClose();
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  const bobDiameter = PENDULUM_BOB_DIAMETER_PX * PREVIEW_SCALE;
  const logoDiameter = tempSize * PREVIEW_SCALE;
  const showsUpscaleWarning =
    naturalSize !== null &&
    Math.min(naturalSize.width, naturalSize.height) < tempSize;

  return (
    <DSDialog
      open={isOpen}
      maxWidth="xs"
      onClose={handleCancel}
      PaperProps={{ sx: { minWidth: { xs: '90vw', md: '420px' } } }}
    >
      <DSDialogTitle>
        <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DSHeaderTypography>Pendulum logo size</DSHeaderTypography>
          <DSValueTypography>
            Preview is to scale with the physical clock&apos;s pendulum bob.
            Actual bob color depends on the klydo playing at the time.
          </DSValueTypography>
        </DSBox>
      </DSDialogTitle>

      <DSDialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: spacing(4),
          py: spacing(4),
        }}
      >
        <DSBox
          sx={{
            position: 'relative',
            width: `${bobDiameter}px`,
            height: `${bobDiameter}px`,
            borderRadius: '50%',
            backgroundColor: PREVIEW_BOB_COLOR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Pendulum logo preview"
              onLoad={(e) => {
                const img = e.currentTarget;
                setNaturalSize({
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                });
              }}
              style={{
                width: `${logoDiameter}px`,
                height: `${logoDiameter}px`,
                objectFit: 'contain',
              }}
            />
          )}
        </DSBox>

        <DSBox sx={{ width: '100%', px: spacing(2) }}>
          <DSBox
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: spacing(1),
            }}
          >
            <DSValueTypography>Size</DSValueTypography>
            <DSValueTypography sx={{ color: palette.text.secondary }}>
              {tempSize}px
            </DSValueTypography>
          </DSBox>
          <DSSlider
            value={tempSize}
            min={PENDULUM_LOGO_SIZE_MIN}
            max={PENDULUM_LOGO_SIZE_MAX}
            onChange={(_, value) => setTempSize(value as number)}
          />
          {showsUpscaleWarning && (
            <DSValueTypography sx={{ color: palette.error?.main, mt: 1 }}>
              This size is larger than the uploaded logo&apos;s own resolution -
              it will be stretched and may look blurry.
            </DSValueTypography>
          )}
        </DSBox>
      </DSDialogContent>

      <DSDialogActions sx={{ justifyContent: 'space-between', padding: 3 }}>
        <DSSecondaryButton onClick={handleReset}>Reset</DSSecondaryButton>
        <DSBox sx={{ display: 'flex', gap: 2 }}>
          <DSSecondaryButton onClick={handleCancel}>Cancel</DSSecondaryButton>
          <DSPrimaryButton onClick={handleSave}>Save</DSPrimaryButton>
        </DSBox>
      </DSDialogActions>
    </DSDialog>
  );
};

export default PendulumLogoSizeModal;
